import { z } from 'zod'
import { SPECIALIZATION_VALUES } from '@/config/specializations'

// ─── Constants ───────────────────────────────────────────────

export const BUDGET_TYPES = ['hourly', 'fixed', 'monthly'] as const
export type BudgetType = (typeof BUDGET_TYPES)[number]

export const LOCATION_TYPES = ['remote', 'onsite', 'hybrid'] as const
export type LocationType = (typeof LOCATION_TYPES)[number]

export const JOB_STATUSES = ['draft', 'published', 'closed'] as const
export type JobStatusValue = (typeof JOB_STATUSES)[number]

export const APPLICATION_STATUSES = ['pending', 'shortlisted', 'dismissed', 'withdrawn', 'closed'] as const
export type ApplicationStatusValue = (typeof APPLICATION_STATUSES)[number]

export const JOB_TYPES = ['full_time', 'part_time', 'contract', 'project', 'hourly', 'internship'] as const
export type JobTypeValue = (typeof JOB_TYPES)[number]

export const JOB_TYPE_CONFIG = {
  full_time: { label: 'Full-time', color: 'var(--color-brand-indigo)' },
  part_time: { label: 'Part-time', color: 'var(--color-brand-violet)' },
  contract: { label: 'Contract', color: 'var(--color-accent)' },
  project: { label: 'Project', color: 'var(--color-status-success)' },
  hourly: { label: 'Hourly', color: 'var(--color-status-warning)' },
  internship: { label: 'Internship', color: 'var(--color-text-muted)' },
} as const satisfies Record<JobTypeValue, { label: string; color: string }>

// ─── Create Job Schema ──────────────────────────────────────

export const createJobSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(5, 'Title must be at least 5 characters')
      .max(100, 'Title must be 100 characters or fewer'),
    description: z
      .string()
      .trim()
      .min(50, 'Description must be at least 50 characters')
      .max(5000, 'Description must be 5,000 characters or fewer'),
    budgetType: z.enum(BUDGET_TYPES, { message: 'Select a budget type' }),
    budgetMin: z
      .number({ message: 'Enter a minimum budget' })
      .int('Budget must be a whole number')
      .min(1, 'Minimum budget must be at least $1'),
    budgetMax: z
      .number({ message: 'Enter a maximum budget' })
      .int('Budget must be a whole number')
      .min(1, 'Maximum budget must be at least $1'),
    locationType: z.enum(LOCATION_TYPES, { message: 'Select a location type' }),
    location: z
      .string()
      .trim()
      .max(100, 'Location must be 100 characters or fewer')
      .optional()
      .or(z.literal('')),
    specializations: z
      .array(z.enum(SPECIALIZATION_VALUES))
      .min(1, 'Select at least one specialization')
      .max(10, 'Maximum 10 specializations'),
    jobType: z.enum(JOB_TYPES, { message: 'Select a job type' }).default('full_time'),
    deadline: z
      .string()
      .datetime({ message: 'Invalid deadline date' })
      .optional()
      .or(z.literal('')),
  })
  .refine((data) => data.budgetMax >= data.budgetMin, {
    message: 'Maximum budget must be greater than or equal to minimum',
    path: ['budgetMax'],
  })
  .refine(
    (data) =>
      data.locationType === 'remote' ||
      (data.location !== undefined && data.location !== '' && data.location.trim().length > 0),
    {
      message: 'Location is required for on-site and hybrid roles',
      path: ['location'],
    },
  )
  .refine(
    (data) => {
      if (!data.deadline || data.deadline === '') return true
      return new Date(data.deadline) > new Date()
    },
    {
      message: 'Deadline must be in the future',
      path: ['deadline'],
    },
  )

export type CreateJobInput = z.infer<typeof createJobSchema>

// ─── Update Job Schema ──────────────────────────────────────

export const updateJobSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(5, 'Title must be at least 5 characters')
      .max(100, 'Title must be 100 characters or fewer')
      .optional(),
    description: z
      .string()
      .trim()
      .min(50, 'Description must be at least 50 characters')
      .max(5000, 'Description must be 5,000 characters or fewer')
      .optional(),
    budgetType: z.enum(BUDGET_TYPES).optional(),
    budgetMin: z
      .number()
      .int('Budget must be a whole number')
      .min(1, 'Minimum budget must be at least $1')
      .optional(),
    budgetMax: z
      .number()
      .int('Budget must be a whole number')
      .min(1, 'Maximum budget must be at least $1')
      .optional(),
    locationType: z.enum(LOCATION_TYPES).optional(),
    location: z
      .string()
      .trim()
      .max(100, 'Location must be 100 characters or fewer')
      .optional()
      .or(z.literal('')),
    specializations: z
      .array(z.enum(SPECIALIZATION_VALUES))
      .min(1, 'Select at least one specialization')
      .max(10, 'Maximum 10 specializations')
      .optional(),
    jobType: z.enum(JOB_TYPES).optional(),
    deadline: z
      .string()
      .datetime({ message: 'Invalid deadline date' })
      .optional()
      .or(z.literal(''))
      .or(z.null()),
  })
  .refine(
    (data) => {
      if (data.budgetMin !== undefined && data.budgetMax !== undefined) {
        return data.budgetMax >= data.budgetMin
      }
      return true
    },
    {
      message: 'Maximum budget must be greater than or equal to minimum',
      path: ['budgetMax'],
    },
  )
  .refine(
    (data) => {
      if (data.locationType && data.locationType !== 'remote') {
        return data.location !== undefined && data.location !== '' && data.location.trim().length > 0
      }
      return true
    },
    {
      message: 'Location is required for on-site and hybrid roles',
      path: ['location'],
    },
  )

export type UpdateJobInput = z.infer<typeof updateJobSchema>

// ─── Apply Schema ───────────────────────────────────────────

export const APPLICATION_ATTACHMENT_KINDS = ['prompt', 'package'] as const
export type ApplicationAttachmentKind = (typeof APPLICATION_ATTACHMENT_KINDS)[number]

export const MAX_APPLICATION_ATTACHMENTS = 3

export const applicationAttachmentInputSchema = z.object({
  kind: z.enum(APPLICATION_ATTACHMENT_KINDS, { message: 'Invalid attachment kind' }),
  id: z.string().uuid('Invalid attachment id'),
})

export type ApplicationAttachmentInput = z.infer<typeof applicationAttachmentInputSchema>

export const applySchema = z.object({
  jobId: z.string().uuid('Invalid job ID'),
  coverLetter: z
    .string()
    .trim()
    .max(2000, 'Cover letter must be 2,000 characters or fewer')
    .optional()
    .or(z.literal('')),
  attachments: z
    .array(applicationAttachmentInputSchema)
    .max(MAX_APPLICATION_ATTACHMENTS, `You can attach at most ${MAX_APPLICATION_ATTACHMENTS} portfolio items`)
    .optional(),
})

export type ApplyInput = z.infer<typeof applySchema>

// ─── Application Status Update Schema ───────────────────────

export const updateApplicationStatusSchema = z.object({
  applicationId: z.string().uuid('Invalid application ID'),
  status: z.enum(
    ['new', 'reviewed', 'shortlisted', 'assessment', 'interview', 'offered', 'hired', 'rejected', 'withdrawn', 'closed'],
    { message: 'Invalid status' },
  ),
})

export type UpdateApplicationStatusInput = z.infer<typeof updateApplicationStatusSchema>
